import React, {FC, memo, useMemo, useState} from "react";
import {Button, ButtonProps, Drawer, Skeleton, Tabs, Tooltip} from "antd";
import {HistoryOutlined, SyncOutlined} from "@ant-design/icons";
import {ApolloError} from "apollo-client";
import {
    Course,
    PaymentInterval,
    useCourseHistoryByCourseIdQuery,
    useBookingHistoryByCourseIdQuery,
    useGetCourseTypesQuery,
    useGetLocationsQuery,
} from "../../generated/graphql";
import useHighestRole from "../../helpers/useHighestRole";
import Roles from "../../../shared/models/roles";
import {atLeast} from "../common/AuthenticateSetup";
import CourseHistoryContent, {EntityLookupMaps} from "./CourseHistoryContent";
import BookingHistoryCourseContent from "../bookingHistory/CourseHistoryContent";
import css from "../bookingHistory/BookingHistory.less";

const {TabPane} = Tabs;

type CombinedCourseHistoryDrawerProps = {
    courseId?: Course["id"];
    courseNumber?: string;
    buttonProps?: ButtonProps;
    buttonStyle?: React.CSSProperties;
    tooltipPlacement?: "left" | "right" | "top" | "bottom";
    defaultTab?: "course" | "bookings";
};

/**
 * Kombinierter Drawer fuer Kurs-Historie und Buchungs-Historie
 * - Tab "Kurshistorie": Zeigt Aenderungen am Kurs selbst (fuer Admin und hoeher)
 * - Tab "Buchungshistorie": Zeigt Buchungsaenderungen des Kurses (fuer alle Rollen)
 */
const CombinedCourseHistoryDrawer: FC<CombinedCourseHistoryDrawerProps> = ({
    courseId,
    courseNumber,
    buttonProps,
    buttonStyle,
    tooltipPlacement = "left",
    defaultTab,
}) => {
    const highestRole = useHighestRole();
    const [open, setOpen] = useState(false);
    const [refetching, setRefetching] = useState(false);

    // Admin und hoeher haben Zugriff auf den Kurshistorie-Tab (hierarchie-basiert,
    // deckungsgleich mit dem Backend @Authorized([Admin, Superuser])).
    const hasCourseHistoryAccess = atLeast(Roles.Admin).includes(highestRole);

    // Aktiver Tab: Fuer Benutzer mit Zugriff auf Kurshistorie ist dieser der Default
    const effectiveDefaultTab = defaultTab ?? (hasCourseHistoryAccess ? "course" : "bookings");
    const [activeTab, setActiveTab] = useState<"course" | "bookings">(effectiveDefaultTab);

    // === Booking History Query (fuer alle Rollen) ===
    const {
        data: bookingHistoryData,
        loading: bookingHistoryLoading,
        error: bookingHistoryError,
        refetch: refetchBookingHistory,
    } = useBookingHistoryByCourseIdQuery({
        skip: !courseId || !open,
        variables: {courseId: courseId ?? ""},
        fetchPolicy: "network-only",
    });

    // === Course History Query (nur fuer berechtigte Rollen, lazy loading) ===
    const {
        data: courseHistoryData,
        loading: courseHistoryLoading,
        error: courseHistoryError,
        refetch: refetchCourseHistory,
    } = useCourseHistoryByCourseIdQuery({
        skip: !courseId || !open || !hasCourseHistoryAccess,
        variables: {courseId: courseId ?? ""},
        fetchPolicy: "network-only",
    });

    // === CourseTypes und Locations fuer UUID-Aufloesung (nur wenn Kurshistorie-Tab) ===
    const {data: courseTypesData} = useGetCourseTypesQuery({
        skip: !open || !hasCourseHistoryAccess,
    });

    const {data: locationsData} = useGetLocationsQuery({
        skip: !open || !hasCourseHistoryAccess,
    });

    /**
     * Erstellt Lookup-Maps fuer die Aufloesung von UUIDs zu lesbaren Namen
     */
    const lookupMaps = useMemo((): EntityLookupMaps => {
        const courseTypes: Record<string, string> = {};
        const locations: Record<string, string> = {};
        const venues: Record<string, string> = {};

        if (courseTypesData?.courseTypes) {
            courseTypesData.courseTypes.forEach((ct) => {
                courseTypes[ct.id] = ct.name;
            });
        }

        if (locationsData?.locations) {
            locationsData.locations.forEach((loc) => {
                locations[loc.id] = loc.name;
                if (loc.venues) {
                    loc.venues.forEach((venue) => {
                        venues[venue.id] = venue.name;
                    });
                }
            });
        }

        return {courseTypes, locations, venues};
    }, [courseTypesData, locationsData]);

    const courseHistory = courseHistoryData?.courseHistoryByCourseId ?? [];
    const bookingHistory = bookingHistoryData?.bookingHistoryByCourseId ?? [];

    // Extrahiere Kurs-Metadaten aus dem ersten History-Item
    const courseFromHistory = courseHistory[0]?.course;
    const lessonsCount = courseFromHistory?.lessons?.length ?? 0;
    const currentPaymentInterval = courseFromHistory?.paymentInterval ?? undefined;

    const handleRefetch = async () => {
        setRefetching(true);
        const refetchPromises: Promise<unknown>[] = [refetchBookingHistory()];
        if (hasCourseHistoryAccess) {
            refetchPromises.push(refetchCourseHistory());
        }
        await Promise.all(refetchPromises);
        setRefetching(false);
    };

    const isLoading = (activeTab === "course" ? courseHistoryLoading : bookingHistoryLoading) || refetching;
    const currentError = activeTab === "course" ? courseHistoryError : bookingHistoryError;

    // Button immer anzeigen (beide Tabs sind fuer alle sichtbar, nur Kurshistorie-Tab hat Inhaltsbeschraenkung)
    if (!open) {
        return (
            <Tooltip title="Historie anzeigen" placement={tooltipPlacement}>
                <Button
                    {...buttonProps}
                    onClick={() => setOpen(true)}
                    icon={<HistoryOutlined style={{fontSize: buttonStyle?.fontSize}} />}
                    style={buttonStyle}
                />
            </Tooltip>
        );
    }

    const LoadingContent = ({loading: isContentLoading}: {loading: boolean}) => {
        if (!isContentLoading) {
            return null;
        }

        return (
            <div>
                <Skeleton active />
                <Skeleton active />
                <Skeleton active />
            </div>
        );
    };

    const ErrorContent = ({error}: {error?: Error}) => {
        if (!error) {
            return null;
        }

        return (
            <div style={{color: "red", padding: 16}}>
                Fehler beim Laden der Historie: {error.message}
            </div>
        );
    };

    const RefetchButton = () => {
        return <Button icon={<SyncOutlined spin={isLoading} />} type="link" onClick={handleRefetch} />;
    };

    const drawerTitle = courseNumber ? `Historie: ${courseNumber}` : "Historie";

    // Wenn nur ein Tab verfuegbar ist (keine Kurshistorie-Berechtigung), zeige Inhalt ohne Tabs.
    // Bug 3: Wenn der Aufrufer explizit `defaultTab="course"` angefordert hat (z.B. ueber den
    // "In Kurshistorie ansehen"-Button im Booking-Detail-Modal), wuerde dieser Pfad eine
    // Buchungshistorie zeigen - was den Erwartungen widerspricht. Auf Aufrufer-Seite
    // (BookingHistoryDetails.tsx) wird der Quicklink-Button daher fuer nicht-berechtigte
    // Rollen ausgeblendet. Falls dieser Pfad doch erreicht wird, ist der Drawer-Titel
    // sprechend mit "Buchungshistorie", weil das ist, was tatsaechlich angezeigt wird.
    if (!hasCourseHistoryAccess) {
        return (
            <>
                <Tooltip title="Historie anzeigen" placement={tooltipPlacement}>
                    <Button
                        {...buttonProps}
                        onClick={() => setOpen(true)}
                        icon={<HistoryOutlined style={{fontSize: buttonStyle?.fontSize}} />}
                        style={buttonStyle}
                    />
                </Tooltip>
                <Drawer
                    title={`Buchungshistorie: ${courseNumber ?? ""}`}
                    open={open}
                    onClose={() => setOpen(false)}
                    destroyOnClose
                    extra={<RefetchButton />}
                    width="800px"
                    className={css.historyDrawer}
                >
                    <div>
                        <LoadingContent loading={bookingHistoryLoading || refetching} />
                        <ErrorContent error={bookingHistoryError instanceof ApolloError ? bookingHistoryError : undefined} />
                        {!bookingHistoryLoading && !refetching && !bookingHistoryError && (
                            <BookingHistoryCourseContent history={bookingHistory} />
                        )}
                    </div>
                </Drawer>
            </>
        );
    }

    // Drawer mit Tabs fuer berechtigte Benutzer
    return (
        <>
            <Tooltip title="Historie anzeigen" placement={tooltipPlacement}>
                <Button
                    {...buttonProps}
                    onClick={() => setOpen(true)}
                    icon={<HistoryOutlined style={{fontSize: buttonStyle?.fontSize}} />}
                    style={buttonStyle}
                />
            </Tooltip>
            <Drawer
                title={drawerTitle}
                open={open}
                onClose={() => setOpen(false)}
                destroyOnClose
                extra={<RefetchButton />}
                width="800px"
                className={css.historyDrawer}
            >
                <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as "course" | "bookings")}>
                    <TabPane tab="Kurshistorie" key="course">
                        <LoadingContent loading={courseHistoryLoading || refetching} />
                        <ErrorContent error={courseHistoryError instanceof ApolloError ? courseHistoryError : undefined} />
                        {!courseHistoryLoading && !refetching && !courseHistoryError && (
                            <CourseHistoryContent
                                history={courseHistory}
                                lookupMaps={lookupMaps}
                                lessonsCount={lessonsCount}
                                currentPaymentInterval={currentPaymentInterval}
                            />
                        )}
                    </TabPane>
                    <TabPane tab="Buchungshistorie" key="bookings">
                        <LoadingContent loading={bookingHistoryLoading || refetching} />
                        <ErrorContent error={bookingHistoryError instanceof ApolloError ? bookingHistoryError : undefined} />
                        {!bookingHistoryLoading && !refetching && !bookingHistoryError && (
                            <BookingHistoryCourseContent history={bookingHistory} />
                        )}
                    </TabPane>
                </Tabs>
            </Drawer>
        </>
    );
};

export default memo(CombinedCourseHistoryDrawer);
